import { Controller } from "@hotwired/stimulus"

// The Add/Edit Note form in the bill review drawer. Shows the fields that only make sense for the
// chosen status and reasons, and clears the ones it hides so a stale value can't be submitted.
export default class extends Controller {
  static values = {
    // Reasons whose edit names a replacement code (BillReviewNote::EDIT_REASONS_WITH_SUGGESTED_CODE).
    codeReasons: { type: Array, default: ["wrong code"] },
    // The reason whose edit carries a corrected quantity.
    quantityReason: { type: String, default: "incorrect quantity" },
    // The drawer was opened to write a note (the row's Flag button), not to read the line's
    // history: bring the form into view below it.
    compose: { type: Boolean, default: false }
  }

  connect() {
    this.status = this.element.querySelector("[name='status']");
    this.checkboxes = this.element.querySelector("#checkboxes");
    this.suggestedCode = this.element.querySelector("[name='suggested_code']");
    this.suggestedCodeError = this.element.querySelector("#suggested-code-error");
    this.suggestedQuantity = this.element.querySelector("[name='suggested_quantity']");
    this.publicNote = this.element.querySelector("[name='public_note']");

    this.onStatusChanged();
    if (this.composeValue) this.showForm();
  }

  // After the drawer has laid the form out: scroll it to the top of the drawer and put the
  // keyboard on its first field. Only the drawer's own scrolling area moves: scrollIntoView and
  // a scrolling focus() also scroll every ancestor, which can shift the whole drawer out of view
  // (see the guard in drawer_controller.js).
  showForm() {
    requestAnimationFrame(() => {
      const scroller = this.scrollContainer();
      if (scroller) {
        scroller.scrollTop += this.element.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      }
      const first = this.element.querySelector("input[name='edit_reasons[]']") || this.status;
      if (first) first.focus({ preventScroll: true });
    });
  }

  // The nearest ancestor that scrolls its content vertically, looking no further than the drawer
  // panel: a drawer short enough not to scroll must not move the page behind it.
  scrollContainer() {
    const panel = this.element.closest("[data-drawer-target='panel']");
    for (let node = this.element.parentElement; node; node = node === panel ? null : node.parentElement) {
      const overflowY = window.getComputedStyle(node).overflowY;
      if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) return node;
    }
    return null;
  }

  onStatusChanged() {
    const isEdited = this.status.value === "edited";
    this.checkboxes.classList.toggle("hidden", !isEdited);
    this.publicNote.classList.toggle("hidden", !isEdited);
    this.refreshReasonFields();
  }

  onReasonsChanged() {
    this.refreshReasonFields();
  }

  // suggested_code only makes sense when the reviewer says the existing code is wrong, and
  // suggested_quantity only when they say the quantity is. Each input (and the code's inline
  // error slot) shows when status is "edited" AND one of its reasons is checked.
  refreshReasonFields() {
    const isEdited = this.status.value === "edited";
    const showCode = isEdited && this.codeReasonsValue.some(reason => this.isChecked(reason));
    const showQuantity = isEdited && this.isChecked(this.quantityReasonValue);

    this.toggleField(this.suggestedCode, showCode);
    if (this.suggestedCodeError) {
      this.suggestedCodeError.classList.toggle("hidden", !showCode);
    }
    this.toggleField(this.suggestedQuantity, showQuantity);
  }

  isChecked(reason) {
    return [...this.element.querySelectorAll("input[name='edit_reasons[]']")]
      .some(box => box.value === reason && box.checked);
  }

  toggleField(field, shouldShow) {
    if (!field) return;
    field.classList.toggle("hidden", !shouldShow);
    if (!shouldShow) field.value = "";
  }
}
